import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { SlideUpService } from '../../../services/slide-up.service';
import { CommentService } from '../../../services/comment.service';
import { Comment, Commentable } from '../comment.interface';

@Component({
  selector: 'ut-comment-section-short',
  templateUrl: './comment-section-short.component.html',
  styleUrls: ['./comment-section-short.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommentSectionShortComponent implements OnInit, OnDestroy {
  @Input() commentable!: Commentable;

  comments: Comment[] = [];
  newComment: any = { content: '' };
  slideUpId = '';
  currentCommentKey: string | null = null;
  mode = '';
  emojiImages: any;
  editor: any = {};

  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private slideUpService: SlideUpService,
    private sanitizer: DomSanitizer,
    @Inject('$filter') private $filter: any,
    @Inject('StatusService') private StatusService: any,
    private CommentService: CommentService,
    @Inject('AlertService') private AlertService: any,
    @Inject('ServerMessages') private ServerMessages: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('commentEmojis') commentEmojis: any,
  ) {
    this.emojiImages = commentEmojis.emojis;
  }

  ngOnInit(): void {
    this.slideUpId = `commentSectionShortSlideUp_${this.commentable.type}_${this.commentable.id}`;
    this.loadComments();
  }

  setComment(comment: any): void {
    this.newComment = comment;
  }

  loadComments(): void {
    const markdownComments = (comments: Comment[]) => {
      this.comments = [];
      comments.forEach((o) => {
        const markdownHtml = this.$filter('uMarkdown')(o.content);
        o.content_built = this.sanitizer.bypassSecurityTrustHtml(markdownHtml);
        o.indentation = 0;
        this.comments.push(o);
      });
      this.cdr.markForCheck();
    };

    if (this.commentable.recent_comments) {
      markdownComments(this.commentable.recent_comments);
    } else {
      this.StatusService.commentsPage(this.commentable.id, 1, '-created_at', 2).then((res: any) => {
        markdownComments(res.data);
      });
    }
  }

  submitComment(): void {
    const trimmedComment = this.newComment.content?.trim();
    const EMPTY_CONTENT_MARKDOWNS = ['> \n\n'];
    if (!trimmedComment || EMPTY_CONTENT_MARKDOWNS.includes(trimmedComment)) {
      this.AlertService.addDanger("Can't post empty comment!");
      return;
    }

    this.CommentService.postComment(this.commentable.type, this.commentable.id, this.newComment).then(
      () => {
        this.loadComments();

        if (this.commentable.comments) {
          this.commentable.comments.count += 1;
        }

        this.AlertService.add('Comment successfully posted');
        // Close the slide-up form after successful submission
        this.slideUpService.close(this.slideUpId);
        this.currentCommentKey = null;
      },
      (response: any) => {
        this.AlertService.addDanger(this.ServerMessages.prettyMessage(response));
      },
    );
  }

  selectEmoji(emojiCode: string): void {
    this.setMode('');
    let space = '';
    if (!this.newComment.content) {
      this.newComment.content = '';
    } else {
      space = this.newComment.content.slice(-1) === ' ' ? '' : ' ';
    }
    this.newComment.content += space + emojiCode;
  }

  setMode(mode: string): void {
    this.mode = this.mode === mode ? '' : mode;
  }

  openCommentEditor(comment: any = { content: '' }): boolean {
    if (this.LightTesterErrorService.show()) return false;

    // Generate a unique key for this comment to determine if it's the same as what's currently being edited
    const newCommentKey = this.getCommentKey(comment);
    const isOpen = this.slideUpService.isOpen(this.slideUpId);
    const isSameComment = this.currentCommentKey === newCommentKey;

    if (isOpen && isSameComment) {
      // Same comment is already being edited - toggle closed
      this.slideUpService.close(this.slideUpId);
      this.currentCommentKey = null;
    } else {
      // Different comment or panel is closed - set content and ensure panel is open
      this.setComment(comment);
      this.slideUpService.open(this.slideUpId);
      this.currentCommentKey = newCommentKey;
    }

    return true;
  }

  closeCommentForm(): void {
    this.slideUpService.close(this.slideUpId);
    this.currentCommentKey = null;
  }

  private getCommentKey(comment: any): string {
    // Generate a unique key for a comment based on its identity
    if (comment.id) {
      // Editing an existing comment
      return `edit-${comment.id}`;
    }
    if (comment.parent_id) {
      // Reply to a specific comment
      return `reply-${comment.parent_id}`;
    }
    // New top-level comment
    return 'new-comment';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
